package floris.graphics.backoffice.pages.admin.contributors.manage

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import floris.graphics.backoffice.components.ImageChooser
import floris.graphics.backoffice.components.layouts.PageLayout
import floris.graphics.backoffice.components.sections.DetailsSection
import floris.graphics.core.constants.Res
import com.materialdesignsystem.components.widgets.DangerFilledButton
import com.materialdesignsystem.components.widgets.DsBorderRadius
import com.materialdesignsystem.components.widgets.DsEditableArea
import com.materialdesignsystem.components.widgets.DsEditableField
import com.materialdesignsystem.components.widgets.DsReadOnlyField
import com.materialdesignsystem.components.widgets.FilledButton
import com.materialdesignsystem.components.widgets.TextButton
import com.materialdesignsystem.constants.Identifiers
import kotlinx.browser.window
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

@Page("/admin/contributors/manage")
@Composable
fun ManageContributorPage() {

    val context = rememberPageContext()
    val contributorId = remember(context.route) {
        context.route.params["id"]
    }

    val viewModel = remember(contributorId) { ManageContributorViewModel(contributorId) }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.eventFlow.collect {
            when (it) {
                ManageContributorUiEvent.NavigateBack -> {
                    // TODO [TicketNumber]: Route is still in the history stack, so we need to remove it...
                    window.history.back()
                }

                ManageContributorUiEvent.ShowError -> TODO()
            }
        }
    }

    ManageContributorPageContent(
        state = state,
        onEvent = viewModel::onEvent
    )
}

@Composable
private fun ManageContributorPageContent(
    state: ManageContributorState,
    onEvent: (ManageContributorEvent) -> Unit
) {
    val breakpoint = rememberBreakpoint()

    PageLayout(if (state.isCreationFlow) "Contributeur aanmaken" else "Contributeur bewerken") {
        Column(
            modifier = Modifier
                .fillMaxWidth(if (breakpoint > Breakpoint.LG) 60.percent else 90.percent)
                .fillMaxHeight()
                .maxWidth(800.px)
                .padding(topBottom = 58.px),
            verticalArrangement = Arrangement.spacedBy(24.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            ImageChooser(
                modifier = Modifier.size(200.px),
                id = "logo_chooser",
                label = "Contributor Logo",
                imageUrl = state.logoUrl ?: state.originalContributor.logoMediaFile?.url?.toString(),
                centerText = "Klik hier om een logo te kiezen",
                isLoading = state.uploadingLogo,
                onFileSelected = { onEvent(ManageContributorEvent.UpdateLogo(it)) },
                onDeleteClicked = { onEvent(ManageContributorEvent.RemoveLogo) }
            )

            DsEditableField(
                modifier = Modifier.fillMaxWidth(),
                id = Identifiers.ManageContributors.Name,
                label = Res.String.Name,
                placeholder = Res.String.EnterName,
                value = state.insertOrUpdateContributor.name,
                onValueChange = { onEvent(ManageContributorEvent.UpdateName(it)) }
            )

            DsEditableField(
                modifier = Modifier.fillMaxWidth(),
                id = Identifiers.ManageContributors.Website,
                label = Res.String.Website,
                placeholder = Res.String.EnterWebsite,
                value = state.insertOrUpdateContributor.website ?: "",
                onValueChange = { onEvent(ManageContributorEvent.UpdateWebsite(it)) },
                valid = state.isValidWebsite
            )

            DsEditableArea(
                modifier = Modifier.fillMaxWidth(),
                id = Identifiers.ManageContributors.Bio,
                label = Res.String.Bio,
                placeholder = Res.String.EnterBio,
                value = state.insertOrUpdateContributor.bio ?: "",
                onValueChange = { onEvent(ManageContributorEvent.UpdateBio(it)) }
            )

            if (state.originalContributor.id.isNotBlank()) {
                DetailsSection(
                    modifier = Modifier.fillMaxWidth(),
                    title = Res.String.Metadata
                ) {
                    Column(Modifier.fillMaxWidth(), Arrangement.spacedBy(16.px)) {
                        DsReadOnlyField(
                            modifier = Modifier.fillMaxWidth(),
                            label = Res.String.ID,
                            value = state.originalContributor.id
                        )

                        DsReadOnlyField(
                            modifier = Modifier.fillMaxWidth(),
                            label = Res.String.CreatedAt,
                            value = state.originalContributor.createdAt
                        )

                        DsReadOnlyField(
                            modifier = Modifier.fillMaxWidth(),
                            label = Res.String.UpdatedAt,
                            value = state.originalContributor.updatedAt
                        )

                        DangerFilledButton(
                            onClick = { onEvent(ManageContributorEvent.Delete) }
                        ) {
                            Text(Res.String.Delete)
                        }
                    }
                }
            }

            Row(horizontalArrangement = Arrangement.spacedBy(16.px)) {
                FilledButton(
                    enabled = state.hasChanged,
                    onClick = { onEvent(ManageContributorEvent.Save) }
                ) {
                    Text(Res.String.Save)
                }
                TextButton(
                    borderRadius = DsBorderRadius(8.px),
                    onClick = { onEvent(ManageContributorEvent.Cancel) }
                ) {
                    Text(Res.String.Cancel)
                }
            }
        }
    }
}
